import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, map, of } from 'rxjs';
import { environment } from 'src/environments/environment.prod';
import { BrowserService } from 'src/_lib/service/browser.service';
import {
  CategoriesDataDapInterface,
  CategoriesDataInterface,
  CategoryByIDPostDataInterface,
  CategoryFilterPostDataInterface,
  Common
} from 'src/_lib/interfaces/category.interface';

@Injectable({
  providedIn: 'root',
})
export class CategoryService {
  endpoint: string = environment.apiUrl;
  allMainCatDetails: any = [];
  allParentCats: any = [];
  homeEngineCache$: any = [];
  allCategoriesCache$?: Observable<any> | null = null;
  shopByCategoryCache$?: Observable<any> | null = null;
  gotAHobbyCache$?: Observable<any> | null = null;
  homePowerHouseCache$?: Observable<any> | null = null;
  categoryBreadCrumbNames: string[] = [];
  categoryBreadCrumbKeys: number[] = [];

  constructor(private browser: BrowserService, private http: HttpClient) { }

  allcategoriesList(): Observable<any> {
    /*const vehicle_form ={
      select_make: "",
      select_model: "",
      select_year: "",
      sub_model_1:"",
      sub_model_2:"",
      sub_model_3:""
    };*/
    if (this.allCategoriesCache$) return this.allCategoriesCache$;
    const storedData = this.getStoredAllCategories();
    if (storedData) {
      this.allCategoriesCache$ = of(storedData).pipe(shareReplay(1));
      return this.allCategoriesCache$;
    }
    //this.allCategoriesCache$ = this.http.post<any>(this.endpoint + 'all-categories', vehicle_form).pipe(
    this.allCategoriesCache$ = this.http.get<any>(this.endpoint + 'all-categories').pipe(
      shareReplay(1),
      map(res => {
        const apiData = res || [];
        this.storeAllCategories(apiData);
        return apiData;
      })
    );
    return this.allCategoriesCache$;
  }

  storeAllCategories(data: any): void {
    this.browser.localStorage?.setItem('allCategoriesData', JSON.stringify(data));
  }

  getStoredAllCategories(): any | null {
    const data = this.browser.localStorage?.getItem('allCategoriesData');
    return data ? JSON.parse(data) : null;
  }

  categoriesList(): Observable<Common<CategoriesDataInterface>> {
    return this.http.get<Common<CategoriesDataInterface>>(this.endpoint + 'categories');
  }

  singlecategory(data: CategoryByIDPostDataInterface): Observable<Common<CategoriesDataInterface>> {
    return this.http.post<Common<CategoriesDataInterface>>(
      this.endpoint + 'category-by-id',
      data
    );
  }

  categoryFilter(data: CategoryFilterPostDataInterface): Observable<any> {
    return this.http.post<any>(this.endpoint + 'categories-filter', data);
  }

  getGotAHobbyCategories(): Observable<any> {
    if (this.gotAHobbyCache$) {
      return this.gotAHobbyCache$;
    }
    const storedData = this.getGotAHobbyFromStorage();
    if (storedData) {
      this.gotAHobbyCache$ = of(storedData).pipe(shareReplay(1));
      return this.gotAHobbyCache$;
    }
    this.gotAHobbyCache$ = this.http
      .get<any>(this.endpoint + 'categories')
      .pipe(shareReplay(1));
    this.gotAHobbyCache$.subscribe(data =>
      this.saveGotAHobbyToStorage(data)
    );
    return this.gotAHobbyCache$;
  }

  saveGotAHobbyToStorage(data: any): void {
    this.browser.localStorage?.setItem('gotAHobbyCategoriesData', JSON.stringify(data));
  }

  getGotAHobbyFromStorage(): any | null {
    const data = this.browser.localStorage?.getItem('gotAHobbyCategoriesData');
    return data ? JSON.parse(data) : null;
  }

  shopByCategory(): Observable<any> {
    if (this.shopByCategoryCache$) {
      return this.shopByCategoryCache$;
    }
    const storedData = this.getShopByCategoryFromStorage();
    if (storedData) {
      this.shopByCategoryCache$ = of(storedData).pipe(shareReplay(1));
      return this.shopByCategoryCache$;
    }
    this.shopByCategoryCache$ = this.http
      .get<any>(this.endpoint + 'parent-subcategories')
      .pipe(shareReplay(1));
    this.shopByCategoryCache$.subscribe(data =>
      this.saveShopByCategoryToStorage(data)
    );
    return this.shopByCategoryCache$;
  }

  saveShopByCategoryToStorage(data: any): void {
    this.browser.localStorage?.setItem('shopByCategoryData', JSON.stringify(data));
  }

  getShopByCategoryFromStorage(): any | null {
    const data = this.browser.localStorage?.getItem('shopByCategoryData');
    return data ? JSON.parse(data) : null;
  }

  singlecategoryDap(data: CategoriesDataDapInterface) {
    return this.http.post(this.endpoint + '/dap-category-by-id', data);
  }

  homeCategoryData(): Observable<any> {
    if (this.homeEngineCache$?.child_cats?.length) {
      return of(this.homeEngineCache$).pipe(shareReplay(1));
    }
    const storedData = this.getHomeEngineFromStorage();
    if (storedData) {
      this.homeEngineCache$ = storedData;
      return of(storedData).pipe(shareReplay(1));
    }
    return this.http.get<any>(this.endpoint + 'home-category?perPage=7').pipe(
      shareReplay(1),
      map(res => {
        const apiData = res?.[0]?.data?.[0] || null;
        if (apiData) {
          this.homeEngineCache$ = apiData;
          this.saveHomeEngineToStorage(apiData);
        }
        return apiData;
      })
    );
  }

  saveHomeEngineToStorage(data: any): void {
    this.browser.localStorage?.setItem('homeEngineData', JSON.stringify(data));
  }

  getHomeEngineFromStorage(): any | null {
    const data = this.browser.localStorage?.getItem('homeEngineData');
    return data ? JSON.parse(data) : null;
  }

  powerHouseMakesCategoryData(): Observable<any> {
    if (this.homePowerHouseCache$) {
      return this.homePowerHouseCache$;
    }
    const storedData = this.getPowerHouseFromStorage();
    if (storedData) {
      this.homePowerHouseCache$ = of(storedData).pipe(shareReplay(1));
      return this.homePowerHouseCache$;
    }
    this.homePowerHouseCache$ = this.http
      .get<any>(this.endpoint + 'top-trending-categories?perPage=7')
      .pipe(shareReplay(1));
    this.homePowerHouseCache$.subscribe(data => {
      this.savePowerHouseToStorage(data);
    });
    return this.homePowerHouseCache$;
  }

  savePowerHouseToStorage(data: any): void {
    this.browser.localStorage?.setItem('homePowerHouseData', JSON.stringify(data));
  }

  getPowerHouseFromStorage(): any | null {
    const data = this.browser.localStorage?.getItem('homePowerHouseData');
    return data ? JSON.parse(data) : null;
  }

  subCategoriesList(category_key: any): Observable<any> {
    return this.http.post<any>(`${this.endpoint}parent-subcat-products`,{category:category_key});
  }

  categoryBySearchResults(keyword: string) {
    return this.http.get<any>(this.endpoint + `categories-search?name=${keyword}`);
  }

  getCategoryBreadcrumb(category_key: string | number): Observable<any> {
    return this.http.post<any>(
      `${this.endpoint}category/breadcrumb`,
      { category_key: category_key }
    );
  }

  setCategoryBreadCrumb(names: string[], keys: number[]) {
    this.categoryBreadCrumbNames = names;
    this.categoryBreadCrumbKeys = keys;
  }

  getCategoryBreadCrumbNames() {
    return this.categoryBreadCrumbNames;
  }

  getCategoryBreadCrumbKeys() {
    return this.categoryBreadCrumbKeys;
  }

  cleanBreadCrumb() {
    this.categoryBreadCrumbNames = [];
    this.categoryBreadCrumbKeys = [];
  }

  setLastCategoryKey(key: number) {
    this.browser.localStorage?.setItem('lastCategoryKey', key.toString());
  }

  getLastCategoryKey(): number | null {
    const val = this.browser.localStorage?.getItem('lastCategoryKey');
    return val ? +val : null;
  }

  clearLastCategoryKey() {
    this.browser.localStorage?.removeItem('lastCategoryKey');
  }

  setLastPageType(type: 'category' | 'product') {
    this.browser.localStorage?.setItem('lastPageType', type);
  }

  getLastPageType(): 'category' | 'product' | null {
    return this.browser.localStorage?.getItem('lastPageType') as 'category' | 'product' | null;
  }
}